import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useTheme,
  Theme,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import {
  IAggregationGroupby,
  IAggregationMetrics,
  IAggregationQuery,
} from "../types";
import AddIcon from "@material-ui/icons/Add";
import TrashIcon from "@material-ui/icons/Delete";
import { useSelector } from "react-redux";
import { selectSelectedCollection } from "../store/collection/collection.selector";
import {
  getGroupingFields,
  getMetricsFields,
} from "../helper/collection.helper";
import { CollectionService } from "../services";
import { DropdownComponent } from "../components";

interface IProps {
  open: boolean;
  onClose: () => void;
}

interface IAggregationInputProps {
  theme: Theme;
  fields: string[];
  aggregation: string[];
  initialGroupField?: string;
  initialAggregationField?: string;
  editable?: boolean;
  onAction: (values?: any) => void;
  onDelete?: () => void;
}

const AggregationInput: React.FC<IAggregationInputProps> = ({
  theme,
  fields,
  aggregation,
  initialAggregationField,
  initialGroupField,
  editable,
  onAction,
  onDelete,
}) => {
  const [groupFieldValue, setGroupFieldValue] = useState<string | null>(null);
  const [aggregationValue, setAggregationValue] = useState<string | null>(null);

  const handleOnAction = () => {
    onAction({
      agg: aggregationValue,
      name: groupFieldValue,
      field: groupFieldValue,
    });
    setGroupFieldValue("");
    setAggregationValue("");
  };

  const handleGroupChange = (value: any) => {
    if (editable) {
      return onAction({
        agg: initialAggregationField,
        name: value,
        field: value,
      });
    }
    setGroupFieldValue(value);
  };

  const handleFieldChange = (value: any) => {
    if (editable) {
      return onAction({
        agg: value,
        name: initialGroupField,
        field: initialGroupField,
      });
    }
    setAggregationValue(value);
  };

  return (
    <Box display="flex" alignItems="center" mb={2} mt={1}>
      <DropdownComponent
        options={fields}
        value={editable ? initialGroupField : groupFieldValue}
        onChange={handleGroupChange}
        placeholder="Select field"
      />

      <DropdownComponent
        options={aggregation}
        value={editable ? initialAggregationField : aggregationValue}
        onChange={handleFieldChange}
        placeholder="Aggregation"
      />

      <IconButton
        disableRipple
        disableFocusRipple
        disableTouchRipple
        style={{ marginLeft: theme.spacing(1) }}
        onClick={editable ? onDelete : handleOnAction}
      >
        {editable ? <TrashIcon color="error" /> : <AddIcon color="primary" />}
      </IconButton>
    </Box>
  );
};

const AggregationQueryModal: React.FC<IProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [groupby, setGroupBy] = useState<IAggregationGroupby[]>([]);
  const [metrics, setMetrics] = useState<IAggregationMetrics[]>([]);
  const selectedCollection = useSelector(selectSelectedCollection);
  const groupingFields = getGroupingFields(
    selectedCollection?.data?.schema || {}
  );
  const metricsFields = getMetricsFields(
    selectedCollection?.data?.schema || {}
  );

  const handlePerformAggregation = () => {
    const query: IAggregationQuery = {
      groupby,
      metrics,
    };

    CollectionService.updateAggregationQuery(query);
    onClose();
  };

  useEffect(() => {
    if (selectedCollection.data) {
      setGroupBy(selectedCollection.data.aggregationQuery.groupby);
      setMetrics(selectedCollection.data.aggregationQuery.metrics);
    }
  }, [selectedCollection]);

  const handleAddGroupBy = (values: any) => {
    setGroupBy([...groupby, values]);
  };

  const handleUpdateGroupBy = (values: any, index: number) => {
    const data = [...groupby];
    data[index] = values;
    setGroupBy([...data]);
  };

  const handleUpdateMetrics = (values: any, index: number) => {
    const data = [...metrics];
    data[index] = values;
    setMetrics([...data]);
  };

  const handleRemoveGroupBy = (index: number) => {
    const data = [...groupby];
    data.splice(index, 1);
    setGroupBy(data);
  };

  const handleRemoveMetrics = (index: number) => {
    const data = [...metrics];
    data.splice(index, 1);
    setMetrics(data);
  };

  const handelAddMetrics = (values: any) => {
    setMetrics([...metrics, values]);
  };

  return (
    <Dialog open={open} fullWidth onClose={onClose}>
      <DialogTitle
        style={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.secondary,
        }}
      >
        Aggregation query
      </DialogTitle>

      <DialogContent>
        <Typography>Group by :</Typography>
        {groupby.map((element, index) => (
          <AggregationInput
            key={element.field}
            theme={theme}
            fields={groupingFields}
            initialGroupField={element.field}
            initialAggregationField={element.agg}
            editable
            onDelete={() => handleRemoveGroupBy(index)}
            aggregation={["category", "texts"]}
            onAction={(values) => handleUpdateGroupBy(values, index)}
          />
        ))}
        {/* {!groupby.length && ( */}
          <AggregationInput
            theme={theme}
            fields={groupingFields}
            aggregation={["category", "texts"]}
            onAction={handleAddGroupBy}
          />
        {/* )} */}
        <Typography>Metrics :</Typography>
        {metrics.map((element, index) => (
          <AggregationInput
            key={element.field}
            theme={theme}
            fields={metricsFields}
            initialGroupField={element.field}
            initialAggregationField={element.agg}
            editable
            onDelete={() => handleRemoveMetrics(index)}
            aggregation={["avg", "max", "min", "sum", "cardinality"]}
            onAction={(values) => handleUpdateMetrics(values, index)}
          />
        ))}
        <AggregationInput
          theme={theme}
          fields={metricsFields}
          aggregation={["avg", "max", "min", "sum", "cardinality"]}
          onAction={handelAddMetrics}
        />
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          style={{
            textTransform: "none",
            borderColor: theme.palette.error.light,
            color: theme.palette.error.light,
          }}
          variant="outlined"
          disableRipple
          onClick={onClose}
        >
          Close
        </Button>
        <Button
          color="primary"
          style={{ textTransform: "none" }}
          variant="outlined"
          disableRipple
          onClick={handlePerformAggregation}
        >
          Perform
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AggregationQueryModal;
